import React, { Component } from "react";
import TabSection from "./TabSection";
import { withRouter } from "react-router-dom";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import { graphql } from "react-apollo";
import * as compose from "lodash.flowright";
import moment from "moment";
import { Row, Col, Modal } from "react-bootstrap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CardLessDataTableWithSearch from "../datatables/CardLessDataTableWithSearch";
import { FeePageShimmer } from "../FeeStructure/FeeShimmer";

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Day status → colour. Codes: P = present (green), A = absent (red),
// NM = not marked (grey). Full words are also supported.
const STATUS_COLORS = {
  p: "#22c55e", present: "#22c55e",
  a: "#ef4444", ab: "#ef4444", absent: "#ef4444",
  nm: "#9ca3af", "not marked": "#9ca3af", notmarked: "#9ca3af",
  l: "#f59e0b", leave: "#f59e0b",
  h: "#0ea5e9", holiday: "#0ea5e9",
  wo: "#cbd5e1", weekoff: "#cbd5e1", sunday: "#cbd5e1",
  hd: "#14b8a6", "half-day": "#14b8a6", halfday: "#14b8a6",
};
const STATUS_LABELS = {
  p: "Present", present: "Present",
  a: "Absent", ab: "Absent", absent: "Absent",
  nm: "Not Marked", "not marked": "Not Marked", notmarked: "Not Marked",
  l: "Leave", leave: "Leave",
  h: "Holiday", holiday: "Holiday",
  wo: "Week Off", weekoff: "Week Off", sunday: "Week Off",
  hd: "Half Day", "half-day": "Half Day", halfday: "Half Day",
};
// Short code shown on each calendar day cell.
const STATUS_ABBR = {
  p: "P", present: "P",
  a: "A", ab: "A", absent: "A",
  nm: "NM", "not marked": "NM", notmarked: "NM",
  l: "L", leave: "L",
  h: "H", holiday: "H",
  wo: "WO", weekoff: "WO", sunday: "WO",
  hd: "HD", "half-day": "HD", halfday: "HD",
};
const key = (s) => `${s || ""}`.trim().toLowerCase();
const statusColor = (s) => STATUS_COLORS[key(s)] || "#e2e8f0";
const statusLabel = (s) => STATUS_LABELS[key(s)] || (s ? String(s) : "—");
const statusAbbr = (s) => STATUS_ABBR[key(s)] || (s ? String(s).slice(0, 2).toUpperCase() : "");

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Colored status badge for the session-wise table.
const statusBadge = (cell) => {
  const status = (cell || "").toLowerCase();
  const palette = {
    present: { bg: "#dcfce7", fg: "#16a34a" },
    absent: { bg: "#fee2e2", fg: "#dc2626" },
    late: { bg: "#ffedd5", fg: "#f97316" },
  };
  const c = palette[status] || { bg: "#f1f5f9", fg: "#64748b" };
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        padding: "4px 14px",
        borderRadius: "20px",
        fontWeight: 600,
        fontSize: "12px",
      }}
    >
      {cell}
    </span>
  );
};

const SESSION_COLUMNS = [
  { dataField: "date", text: "DATE", sort: true },
  { dataField: "day", text: "DAY" },
  { dataField: "period", text: "PERIOD" },
  { dataField: "subject", text: "SUBJECT" },
  { dataField: "status", text: "STATUS", formatter: statusBadge },
];

const GET_MONTHLY_ATTENDANCE_REPORT = gql`
  query getMonthlyAttendanceReport(
    $academic_year_id: Int!
    $institute_id: Int!
    $branch_id: Int!
    $class_id: Int!
    $section_id: String
    $month: String
  ) {
    getMonthlyAttendanceReport(
      academic_year_id: $academic_year_id
      institute_id: $institute_id
      branch_id: $branch_id
      class_id: $class_id
      section_id: $section_id
      month: $month
    ) {
      student_id
      admission_no
      student_name
      student_mobile
      roll_no
      class_id
      section_id
      branch_id
      total_present
      total_absent
      total_working_days
      percentage
      days {
        day
        status
      }
    }
  }
`;

// Session-wise (period-wise) attendance — most recent 30 records.
const GET_PERIODWISE_ATTENDANCE = gql`
  query ($student_user_id: Int, $edu_student_id: Int) {
    getStudentPerformanceReport(
      student_user_id: $student_user_id
      edu_student_id: $edu_student_id
    ) {
      periodwiseattendance(limit: 30) {
        date
        day
        period
        subject
        status
        timestamp
      }
    }
  }
`;

// Overall (all-time) attendance across every month — present/absent/percentage.
const GET_OVERALL_ATTENDANCE = gql`
  query ($student_mobile: String) {
    getStudentOverallAttendance(student_mobile: $student_mobile) {
      total_present
      total_absent
      total_working_days
      total_marked_days
      percentage
    }
  }
`;

// Build the query variables object from the student record + selected month.
// edu_student_id is preferred; fall back to student_user_id when it is null.
const buildVariables = (student, monthMoment) => ({
  academic_year_id: parseInt(student?.academic_year_id) || 0,
  institute_id: parseInt(Cookies.get("institutionid")) || 0,
  branch_id: parseInt(student?.branch) || 0,
  class_id: parseInt(student?.class) || 0,
  section_id: student?.section != null ? String(student.section) : "",
  month: monthMoment.format("YYYY-MM"),
});

// Single shimmering block used to build skeleton placeholders.
const Shimmer = ({ width = "100%", height = "14px", radius = "6px", style = {} }) => (
  <span
    className="sp1-shimmer d-inline-block"
    style={{ width, height, borderRadius: radius, ...style }}
  />
);

const resolveStudentId = (student) => {
  if (!student) return null;
  if (student.edu_student_id !== null && student.edu_student_id !== undefined) {
    return parseInt(student.edu_student_id);
  }
  if (student.student_user_id !== null && student.student_user_id !== undefined) {
    return parseInt(student.student_user_id);
  }
  return null;
};

class AttendanceTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      monthMoment: moment(),
      sessionModalOpen: false, // "Session-wise Detail" popup
      dayFilter: null, // null | "working" | "present" | "absent" — highlights the calendar
    };
  }

  // Does a status pass the active day filter? (null filter = everything shows.)
  matchesFilter = (status) => {
    const f = this.state.dayFilter;
    if (!f) return true;
    const k = key(status);
    if (f === "present") return ["p", "present"].includes(k);
    if (f === "absent") return ["a", "ab", "absent"].includes(k);
    if (f === "working") return ["p", "present", "a", "ab", "absent"].includes(k);
    return true;
  };

  // Clicking a stat card toggles the calendar filter (same as the student app).
  toggleDayFilter = (f) => this.setState((s) => ({ dayFilter: s.dayFilter === f ? null : f }));

  changeMonth = (delta) => {
    // Never allow navigating past the current month.
    if (delta > 0 && this.isAtCurrentMonth()) return;
    const nextMonth = this.state.monthMoment.clone().add(delta, "month");
    this.setState({ monthMoment: nextMonth });
    const report = this.props.getMonthlyAttendanceReport;
    if (report && report.refetch) {
      report.refetch(buildVariables(this.props.student, nextMonth));
    }
  };

  // True when the displayed month is the current (real-world) month.
  isAtCurrentMonth = () =>
    this.state.monthMoment.isSame(moment(), "month");

  // Month picked from the calendar input (value is "YYYY-MM").
  handleMonthInput = (e) => {
    const val = e.target.value;
    if (!val) return;
    const next = moment(val, "YYYY-MM");
    if (!next.isValid() || next.isAfter(moment(), "month")) return;
    this.setState({ monthMoment: next });
    const report = this.props.getMonthlyAttendanceReport;
    if (report && report.refetch) {
      report.refetch(buildVariables(this.props.student, next));
    }
  };

  renderMonthNavigator = () => {
    const nextDisabled = this.isAtCurrentMonth();
    const btnStyle = (disabled) => ({
      width: "34px",
      height: "34px",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      background: "#ffffff",
      color: disabled ? "#cbd5e1" : "#475569",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "not-allowed" : "pointer",
    });
    return (
      <div className="d-flex align-items-center" style={{ gap: "10px" }}>
        <button type="button" style={btnStyle(false)} onClick={() => this.changeMonth(-1)}>
          <i className="fas fa-chevron-left" style={{ fontSize: "12px" }}></i>
        </button>
        <input
          type="month"
          value={this.state.monthMoment.format("YYYY-MM")}
          max={moment().format("YYYY-MM")}
          onChange={this.handleMonthInput}
          className="font-weight-bold text-dark"
          style={{
            fontSize: "14px",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "6px 10px",
            background: "#ffffff",
            color: "#1e293b",
            cursor: "pointer",
          }}
        />
        <button
          type="button"
          style={btnStyle(nextDisabled)}
          onClick={() => this.changeMonth(1)}
          disabled={nextDisabled}
        >
          <i className="fas fa-chevron-right" style={{ fontSize: "12px" }}></i>
        </button>
      </div>
    );
  };

  // Skeleton mirroring the real layout: 4 metric cards + a day grid.
  renderSkeleton = () => (
    <div>
      <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="d-flex align-items-start justify-content-between p-3 flex-grow-1"
            style={{
              minWidth: "220px",
              borderRadius: "16px",
              background: "#ffffff",
              border: "1px solid #f1f2f5",
              boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
            }}
          >
            <div style={{ flex: 1 }}>
              <Shimmer width="60px" height="12px" />
              <Shimmer width="90px" height="30px" radius="8px" style={{ margin: "10px 0" }} />
              <Shimmer width="70px" height="12px" />
            </div>
            <Shimmer width="44px" height="44px" radius="12px" />
          </div>
        ))}
      </div>

      <div><FeePageShimmer /></div>
    </div>
  );

  // Count Present / Absent / Late for the selected month from the day-wise
  // status list, and render them as a bar (column) chart.
  buildChartOptions = (row) => {
    const days = Array.isArray(row?.days) ? row.days : [];
    let present = 0;
    let absent = 0;
    let late = 0;
    let holiday = 0;
    days.forEach((d) => {
      const status = (d.status || "").toLowerCase();
      // Holidays come either from the API status ("H"/"holiday") or fall on a
      // Sunday — represent them separately, not as present.
      const dayDate = this.state.monthMoment.clone().date(parseInt(d.day));
      if (status === "holiday" || status === "h" || dayDate.day() === 0) {
        holiday += 1;
        return;
      }
      if (status === "present" || status === "p") present += 1;
      else if (status === "absent" || status === "a") absent += 1;
      else if (status === "late" || status === "l") late += 1;
    });

    return {
      chart: { type: "column", height: 300, backgroundColor: "transparent" },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: ["Present", "Absent", "Late", "Holiday"],
        lineColor: "#e2e8f0",
        labels: { style: { color: "#64748b", fontWeight: "600" } },
      },
      yAxis: {
        title: { text: null },
        min: 0,
        allowDecimals: false,
        gridLineColor: "#eef2f6",
        labels: { style: { color: "#64748b" } },
      },
      tooltip: { pointFormat: "<b>{point.y}</b> days" },
      plotOptions: {
        column: {
          borderRadius: 6,
          pointPadding: 0.1,
          colorByPoint: true,
          colors: ["#16a34a", "#dc2626", "#f97316", "#8b5cf6"],
          dataLabels: { enabled: true, style: { fontWeight: "600", color: "#334155" } },
        },
      },
      series: [
        {
          name: "Days",
          data: [present, absent, late, holiday],
        },
      ],
    };
  };

  // Attendance Rate donut with an absolutely-centered HTML overlay label
  // ("92% PRESENT"). Uses title:{text:null} + center:["50%","50%"] + overlay div.
  renderDonut = (att) => {
    const present = num(att.total_present);
    const absent = num(att.total_absent);
    const pct = att.percentage != null ? num(att.percentage) : (present + absent ? Math.round((present / (present + absent)) * 100) : 0);
    const color = pct >= 75 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444";
    const options = {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 260,
        spacingTop: 12, spacingBottom: 12,
      },
      title: { text: null },
      credits: { enabled: false },
      tooltip: { enabled: true, pointFormat: "<b>{point.y}</b> days" },
      plotOptions: {
        pie: {
          innerSize: "70%",
          borderWidth: 0,
          size: "80%",
          center: ["50%", "50%"],
          dataLabels: {
            enabled: true,
            distance: 12,
            connectorWidth: 0,
            formatter: function () {
              return this.y > 0 ? `${this.point.name} ${this.y}` : null;
            },
            style: { fontWeight: "700", fontSize: "11px", color: "#475569", textOutline: "none" },
          },
        },
      },
      series: [
        {
          animation: { duration: 900 },
          data: [
            { name: "Present", y: present, color: "#22c55e" },
            { name: "Absent", y: absent, color: "#ef4444" },
          ],
        },
      ],
    };
    return (
      <div style={{ position: "relative" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
        <div
          style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center", pointerEvents: "none", lineHeight: 1.1,
          }}
        >
          <div style={{ fontSize: 26, fontWeight: 800, color }}>{pct}%</div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".5px", color: "#94a3b8" }}>PRESENT</div>
        </div>
      </div>
    );
  };

  // Month grid of day cells (P / WO / NM status codes) with a legend.
  renderCalendar = (att) => {
    const days = att.days || [];
    const byDay = {};
    days.forEach((d) => {
      const n = typeof d.day === "number" ? d.day : (moment(d.day, ["YYYY-MM-DD", "D"]).isValid() ? moment(d.day, ["YYYY-MM-DD", "D"]).date() : parseInt(d.day));
      if (!isNaN(n)) byDay[n] = d.status;
    });
    const first = this.state.monthMoment.clone().startOf("month");
    const daysInMonth = first.daysInMonth();
    const leadOffset = first.day();

    const cells = [];
    for (let i = 0; i < leadOffset; i++) cells.push(<div key={`e${i}`} className="sp-cal-cell is-empty sp-pop" />);
    for (let d = 1; d <= daysInMonth; d++) {
      const status = byDay[d];
      const bg = status ? statusColor(status) : "#eef2f6";
      // When a stat card is active, dim the days that don't match it.
      const dimmed = status ? !this.matchesFilter(status) : !!this.state.dayFilter;
      cells.push(
        <div
          key={d}
          className="sp-cal-cell sp-pop"
          title={status ? `${d} — ${statusLabel(status)}` : `${d}`}
          style={{ background: bg, color: dimmed ? "#cbd5e1" : (status ? "#fff" : "#94a3b8"), opacity: dimmed ? 0.55 : 1, animationDelay: `${d * 12}ms`, flexDirection: "column", lineHeight: 1.05 }}
        >
          <span style={{ fontSize: 13, fontWeight: 700 }}>{d}</span>
          {status ? (
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.3px", opacity: 0.95, marginTop: 1 }}>
              {statusAbbr(status)}
            </span>
          ) : null}
        </div>
      );
    }

    const seen = [...new Set(days.map((d) => key(d.status)).filter(Boolean))];

    return (
      <div>
        {seen.length > 0 && (
          <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: "14px" }}>
            {seen.map((s) => (
              <span key={s} className="d-inline-flex align-items-center text-muted" style={{ fontSize: "12px", gap: 6 }}>
                <span className="d-inline-flex align-items-center justify-content-center text-white" style={{ background: statusColor(s), width: 22, height: 18, borderRadius: 5, fontSize: 10, fontWeight: 700 }}>
                  {statusAbbr(s)}
                </span>
                {statusLabel(s)}
              </span>
            ))}
          </div>
        )}
        <div className="sp-cal-grid mb-3">
          {WEEKDAYS.map((w) => <div key={w} className="sp-cal-head">{w}</div>)}
          {cells}
        </div>
      </div>
    );
  };

  renderTrend = (row, attendancePct) => {
    const eligible = attendancePct >= 75;
    const presentDays = row ? parseInt(row.total_present) || 0 : 0;
    const markedDays = presentDays + (row ? parseInt(row.total_absent) || 0 : 0);
    return (
      <Row className="mt-4">
        <Col lg={8} className="mb-3 mb-lg-0">
          <div
            className="p-3 h-100"
            style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
          >
            <h6
              className="font-weight-bold text-dark mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Monthly Attendance Trend
            </h6>
            {row && Array.isArray(row.days) && row.days.length > 0 ? (
              <HighchartsReact highcharts={Highcharts} options={this.buildChartOptions(row)} />
            ) : (
              <div className="text-muted text-center py-5" style={{ fontSize: "14px" }}>
                No daily data available for this month.
              </div>
            )}
          </div>
        </Col>
        <Col lg={4}>
          <div
            className="p-3 h-100"
            style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
          >
            <h6
              className="font-weight-bold text-dark mb-3"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Status
            </h6>
            <div
              className="d-flex align-items-center p-3"
              style={{
                gap: "12px",
                borderRadius: "12px",
                background: eligible ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${eligible ? "#bbf7d0" : "#fecaca"}`,
              }}
            >
              <div
                className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "9px",
                  background: eligible
                    ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                    : "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
                }}
              >
                <i className={`fas ${eligible ? "fa-check" : "fa-exclamation"}`} style={{ fontSize: "14px" }}></i>
              </div>
              <span
                className="font-weight-bold"
                style={{ fontSize: "14px", color: eligible ? "#16a34a" : "#dc2626" }}
              >
                {eligible
                  ? "Above the 75% threshold — exam-eligible."
                  : "Below the 75% threshold — not exam-eligible."}
              </span>
            </div>
            <p className="text-muted mt-3 mb-0" style={{ fontSize: "13px" }}>
              Present {presentDays} of {markedDays} marked day{markedDays === 1 ? "" : "s"} this month ({attendancePct}%).
            </p>
          </div>
        </Col>
      </Row>
    );
  };

  // "Session-wise Detail" as a popup — kept off the main page (opened via a button)
  // so the period-by-period rows don't clutter the default view.
  renderSessionModal = () => {
    const periodReport = this.props.getPeriodwise || {};
    const sessions = periodReport.getStudentPerformanceReport?.periodwiseattendance || [];
    return (
      <Modal show={this.state.sessionModalOpen} onHide={() => this.setState({ sessionModalOpen: false })} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18 }}>
            Session-wise Detail — recent ({sessions.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "72vh", overflowY: "auto" }}>
          {sessions.length ? (
            <CardLessDataTableWithSearch
              parentData={sessions}
              particlecolumns={SESSION_COLUMNS}
              name="session_wise_attendance"
              placeholder="Search session"
            />
          ) : (
            <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No session-wise records.</div>
          )}
        </Modal.Body>
      </Modal>
    );
  };

  render() {
    const { student } = this.props;
    const report = this.props.getMonthlyAttendanceReport || {};
    const loading = report.loading;
    const list = report.getMonthlyAttendanceReport || [];
    // Overall (all-time) attendance across every month.
    const overall = (this.props.getOverall && this.props.getOverall.getStudentOverallAttendance) || null;

    const studentId = resolveStudentId(student);
    const row = list.find((s) => parseInt(s.student_id) === studentId) || null;

    // Attendance % = present / (present + absent) — the share of MARKED days the
    // student was present. Weekends/holidays/unmarked days are NOT counted against
    // them. (Previously used present / all-calendar-days, which showed 39% for a
    // student present 12 of 13 marked days and wrongly flagged them "not eligible".)
    // Matches the donut's calculation so the two never disagree.
    const totalMonthDays = this.state.monthMoment.daysInMonth();
    const presentDays = row ? parseInt(row.total_present) || 0 : 0;
    const absentDays = row ? parseInt(row.total_absent) || 0 : 0;
    const markedDays = presentDays + absentDays;
    const attendancePct = markedDays > 0 ? Math.round((presentDays / markedDays) * 100) : 0;

    return (
      <TabSection
        icon="fa-calendar-alt"
        title="Attendance"
        description="Daily and monthly attendance records for the student."
        headerRight={this.renderMonthNavigator()}
      >
        <style>{`
          .sp1-shimmer {
            background: #f0f1f3;
            background-image: linear-gradient(to right, #f0f1f3 0%, #e6e8eb 20%, #f0f1f3 40%, #f0f1f3 100%);
            background-repeat: no-repeat;
            background-size: 800px 104px;
            animation: sp1Shimmer 1.2s linear infinite forwards;
          }
          @keyframes sp1Shimmer {
            0% { background-position: -468px 0; }
            100% { background-position: 468px 0; }
          }
        `}</style>
        {loading ? (
          this.renderSkeleton()
        ) : !row ? (
          <div className="text-muted" style={{ fontSize: "14px" }}>
            No attendance found for {student?.student_name || "this student"} in{" "}
            {this.state.monthMoment.format("MMM YYYY")}.
          </div>
        ) : (
          <div>
            {/* Overall (all-time) attendance — spans every month, not just the selected one. */}
            {overall ? (
              <div
                className="d-flex flex-wrap align-items-center justify-content-between p-3 mb-3"
                style={{ borderRadius: "16px", background: "linear-gradient(135deg,#eef2ff 0%,#f5f3ff 100%)", border: "1px solid #e0e7ff", gap: 12 }}
              >
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                  <div className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                    style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg,#6366f1,#4f46e5)" }}>
                    <i className="fas fa-chart-pie" style={{ fontSize: 18 }} />
                  </div>
                  <div>
                    <div className="text-muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".4px" }}>Overall Attendance</div>
                    <div className="text-dark" style={{ fontSize: 13 }}>All-time · {overall.total_present} present of {overall.total_working_days} working day{overall.total_working_days === 1 ? "" : "s"}</div>
                  </div>
                </div>
                <div className="d-flex align-items-center" style={{ gap: 20 }}>
                  <div className="text-center">
                    <div style={{ fontSize: 26, fontWeight: 800, fontFamily: "'Outfit', sans-serif", color: overall.percentage >= 75 ? "#16a34a" : overall.percentage >= 50 ? "#d97706" : "#dc2626" }}>{overall.percentage}%</div>
                    <div className="text-muted" style={{ fontSize: 11, fontWeight: 600 }}>overall</div>
                  </div>
                  <div className="text-center">
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#6366f1" }}>{overall.total_working_days}</div>
                    <div className="text-muted" style={{ fontSize: 11, fontWeight: 600 }}>working</div>
                  </div>
                  <div className="text-center">
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#16a34a" }}>{overall.total_present}</div>
                    <div className="text-muted" style={{ fontSize: 11, fontWeight: 600 }}>present</div>
                  </div>
                  <div className="text-center">
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#ef4444" }}>{overall.total_absent}</div>
                    <div className="text-muted" style={{ fontSize: 11, fontWeight: 600 }}>absent</div>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="text-muted mb-2" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".4px" }}>
              {this.state.monthMoment.format("MMMM YYYY")} — this month
            </div>
            <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
              {[
                {
                  label: "Working Days",
                  value: markedDays,
                  icon: "fa-calendar-day",
                  iconBg: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  note: "present + absent this month",
                  noteColor: "#4f46e5",
                  filter: "working",
                },
                {
                  label: "Attendance",
                  value: `${attendancePct}%`,
                  icon: "fa-calendar-alt",
                  iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  note: `${presentDays} present of ${totalMonthDays} total days`,
                  noteColor: attendancePct >= 75 ? "#16a34a" : "#dc2626",
                },
                {
                  label: "Present",
                  value: row.total_present,
                  icon: "fa-check",
                  iconBg: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                  note: `▲ ${row.total_working_days || 0} working / ${totalMonthDays} total days`,
                  noteColor: "#16a34a",
                  filter: "present",
                },
                {
                  label: "Absent",
                  value: row.total_absent,
                  icon: "fa-times",
                  iconBg: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
                  note: "▲ this term",
                  noteColor: "#16a34a",
                  filter: "absent",
                },
                {
                  label: "Late",
                  value: row.total_late || 0,
                  icon: "fa-clock",
                  iconBg: "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
                  note: "▼ this term",
                  noteColor: "#dc2626",
                },
              ].map((m) => {
                const active = m.filter && this.state.dayFilter === m.filter;
                return (
                <div
                  key={m.label}
                  className="sp-summary-card d-flex align-items-start justify-content-between p-3 flex-grow-1"
                  onClick={m.filter ? () => this.toggleDayFilter(m.filter) : undefined}
                  title={m.filter ? `Highlight ${m.label.toLowerCase()} days in the calendar` : undefined}
                  style={{
                    minWidth: "220px",
                    borderRadius: "16px",
                    background: "#ffffff",
                    border: active ? "1.5px solid #4f46e5" : "1px solid #f1f2f5",
                    boxShadow: active ? "0 8px 20px rgba(79,70,229,0.18)" : "0 6px 18px rgba(0,0,0,0.05)",
                    cursor: m.filter ? "pointer" : "default",
                  }}
                >
                  <div>
                    <span
                      className="d-block text-muted"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      {m.label}
                    </span>
                    <strong
                      className="d-block text-dark"
                      style={{ fontSize: "2rem", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}
                    >
                      {m.value}
                    </strong>
                    <span
                      className="font-weight-bold"
                      style={{ fontSize: "12px", color: m.noteColor }}
                    >
                      {m.note}
                    </span>
                  </div>
                  <div
                    className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "12px",
                      background: m.iconBg,
                      boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                    }}
                  >
                    <i className={`fas ${m.icon}`} style={{ fontSize: "18px" }}></i>
                  </div>
                </div>
                );
              })}
            </div>

            {row ? (
              <Row className="mt-4">
                <Col lg={4} className="mb-3 mb-lg-0">
                  <div
                    className="sp-af-card sp-animate p-4 h-100 d-flex flex-column justify-content-center"
                    style={{ animationDelay: "120ms" }}
                  >
                    <h6
                      className="font-weight-bold text-dark mb-2"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      Attendance Rate
                    </h6>
                    {this.renderDonut(row)}
                  </div>
                </Col>
                <Col lg={8}>
                  <div className="sp-af-card sp-animate p-4 h-100" style={{ animationDelay: "180ms" }}>
                    <h6
                      className="font-weight-bold text-dark mb-3"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      Attendance Calendar
                    </h6>
                    {this.renderCalendar(row)}
                  </div>
                </Col>
              </Row>
            ) : (
              <div className="text-muted mt-4" style={{ fontSize: "14px" }}>
                No attendance data.
              </div>
            )}

            {this.renderTrend(row, attendancePct)}

            {/* Session-wise detail lives in a popup — button keeps the page clean. */}
            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-sm d-inline-flex align-items-center"
                style={{ background: "#f4f0fe", color: "#7c3aed", fontWeight: 700, fontSize: 13, borderRadius: 8, border: "1px solid #e5d9fb", padding: "7px 14px" }}
                onClick={() => this.setState({ sessionModalOpen: true })}
              >
                <i className="fas fa-list-ul mr-2" style={{ fontSize: 12 }} />
                View Session-wise Detail
              </button>
            </div>
            {this.renderSessionModal()}
          </div>
        )}
      </TabSection>
    );
  }
}

export default withRouter(
  compose(
    graphql(GET_MONTHLY_ATTENDANCE_REPORT, {
      options: (props) => ({
        variables: buildVariables(props.student, moment()),
        fetchPolicy: "network-only",
      }),
      name: "getMonthlyAttendanceReport",
    }),
    graphql(GET_PERIODWISE_ATTENDANCE, {
      options: (props) => ({
        variables: {
          student_user_id: parseInt(props.student?.student_user_id) || 0,
          edu_student_id: parseInt(props.student?.edu_student_id) || 0,
        },
        fetchPolicy: "network-only",
      }),
      name: "getPeriodwise",
    }),
    graphql(GET_OVERALL_ATTENDANCE, {
      options: (props) => ({
        variables: { student_mobile: `${(props.student && (props.student.student_mobile || props.student.contact_no || props.student.mobile)) || ""}` },
        fetchPolicy: "network-only",
      }),
      name: "getOverall",
    })
  )(AttendanceTab)
);
